/** Normalised error surfaced to the UI. DB rule violations already carry Turkish messages. */
export class AppError extends Error {
  constructor(
    message: string,
    public readonly code: 'forbidden' | 'not_found' | 'conflict' | 'rule' | 'auth' | 'network' | 'unknown',
    public readonly hint?: string,
    public override readonly cause?: unknown,
    /**
     * The `detail` the raising function attached, verbatim. The posting engine puts
     * a jsonb object there (`insufficient_stock` carries the available quantity, its
     * unit, the location and the lot), and that is the only way a client can say
     * *how much* is on the shelf without a round trip it may not be able to make.
     */
    public readonly detail?: string,
  ) {
    super(message);
    this.name = 'AppError';
  }
}

interface PgLike {
  message?: string;
  code?: string;
  hint?: string | null;
  details?: string | null;
  status?: number;
  name?: string;
  /** storage-js StorageUnknownError: what fetch() threw */
  originalError?: unknown;
}

/**
 * fetch() itself failed (offline, DNS, captive portal, dropped connection) — every engine words it
 * differently: Chrome "Failed to fetch", WebKit (all iOS browsers) "Load failed", Firefox
 * "NetworkError when attempting…", React Native "Network request failed".
 */
const TRANSPORT_MESSAGE = /fetch|network|load failed|internet connection|connection was lost/i;

function isTransportFailure(e: PgLike, msg: string): boolean {
  // auth-js wraps failed or 502–504 requests; the request may simply be repeated
  if (e.name === 'AuthRetryableFetchError') return true;
  // storage-js keeps fetch's TypeError as originalError (its message alone is just "Load failed" on iOS)
  if (e.name === 'StorageUnknownError' && (e.originalError as { name?: string } | undefined)?.name === 'TypeError') return true;
  return TRANSPORT_MESSAGE.test(msg);
}

const networkError = (cause: unknown) => new AppError('Bağlantı yok — internetini kontrol et', 'network', undefined, cause);

export function toAppError(err: unknown): AppError {
  if (err instanceof AppError) return err;
  const e = (err ?? {}) as PgLike;
  const msg = e.message || 'Beklenmeyen bir hata oluştu';
  if (e.name === 'AuthApiError' || e.name === 'AuthError' || e.status === 400 || e.status === 401) {
    if (/invalid login credentials/i.test(msg)) return new AppError('auth.invalidCredentials', 'auth', undefined, err);
    if (/rate limit|too many/i.test(msg)) return new AppError('auth.rateLimited', 'auth', undefined, err);
  }
  switch (e.code) {
    case '42501':
      return new AppError(/row-level security/i.test(msg) ? 'Bu işlem için yetkin yok' : msg, 'forbidden', e.hint ?? undefined, err);
    case 'P0002':
      return new AppError(msg, 'not_found', e.hint ?? undefined, err);
    case '23505':
      return new AppError(msg.includes('duplicate') ? 'Bu kayıt zaten var' : msg, 'conflict', e.hint ?? undefined, err);
    case '23514':
    case '23502':
    case '22023':
      return new AppError(msg, 'rule', e.hint ?? undefined, err, e.details ?? undefined);
  }
  if (isTransportFailure(e, msg)) return networkError(err);
  return new AppError(msg, 'unknown', e.hint ?? undefined, err);
}

/**
 * Would this failure repeat unchanged on every retry? A rule the record breaks, a missing rate or a
 * permission the user does not have stays broken however often it is sent; network and auth
 * failures pass on their own (a dropped link comes back, a session is renewed at the next sign-in).
 * The offline queue parks a permanently failing entry instead of retrying it forever.
 */
export function isPermanentFailure(err: unknown): boolean {
  const { code } = toAppError(err);
  return code === 'rule' || code === 'not_found' || code === 'forbidden' || code === 'conflict';
}

/** Unwraps a supabase-js `{ data, error }` result. */
export function unwrap<T>(res: { data: T; error: unknown; status?: number }): NonNullable<T> {
  // postgrest-js reports a request that never got a response as status 0 (the message is engine-specific)
  if (res.error) throw res.status === 0 ? networkError(res.error) : toAppError(res.error);
  return res.data as NonNullable<T>;
}
